import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import PajakReminderList from "@/components/app/PajakReminderList";
import ChartRangeFilter, { rangeLabel } from "@/components/app/ChartRangeFilter";
import { Package, Scale, Warehouse, Factory } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const Stat = ({ label, value, unit = "", icon: Icon, tone, testid }) => (
  <div data-testid={testid} className="card-flat p-4 md:p-5">
    <div className="flex items-start justify-between">
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`h-9 w-9 flex items-center justify-center rounded-sm ${tone}`}>
        <Icon size={16} className="text-white"/>
      </div>
    </div>
    <div className="mt-3 md:mt-4 font-mono text-lg md:text-2xl tabular-nums text-slate-900">
      {value}{unit && <span className="text-sm text-slate-500 ml-1">{unit}</span>}
    </div>
  </div>
);

export default function DashboardArang() {
  const [d, setD] = useState(null);
  const [trend, setTrend] = useState([]);
  const [produkId, setProdukId] = useState("");
  const [range, setRange] = useState({ key: "default", start: "", end: "" });
  useEffect(() => {
    const params = produkId ? { produk_id: produkId } : {};
    api.get("/dashboard/arang", { params }).then(r => setD(r.data)).catch(() => {});
  }, [produkId]);
  useEffect(() => {
    const params = { ...(produkId ? { produk_id: produkId } : {}), ...(range.start ? { start: range.start, end: range.end } : {}) };
    api.get("/dashboard/arang/trend", { params }).then(r => setTrend(r.data)).catch(() => setTrend([]));
  }, [produkId, range]);

  const kg = (v) => `${Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })} Kg`;
  const perProduk = d?.per_produk || [];
  const selNama = produkId ? (perProduk.find(p => p.produk_id === produkId)?.nama || "") : "Semua Produk";

  const chartData = trend.map(t => ({
    name: t.label || t.bulan.split(" ")[0].slice(0, 3),
    full: t.bulan,
    produksi: t.produksi_kg,
    penjualan: t.penjualan_kg,
    stok: t.stok_kumulatif_kg,
  }));

  return (<>
    <PageHeader title="Dashboard Bisnis Arang" subtitle="Ringkasan produksi, penjualan, dan stok per produk."/>
    <div className="p-4 md:p-8 space-y-6">
      <PajakReminderList/>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 items-start">
        <div data-testid="a-stat-produksi" className="card-flat p-4 md:p-5 sm:row-span-2 lg:row-span-1">
          <div className="flex items-start justify-between">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Total Produksi</div>
            <div className="h-9 w-9 flex items-center justify-center rounded-sm bg-[color:var(--brand)]"><Factory size={16} className="text-white"/></div>
          </div>
          <div className="mt-3 font-mono text-lg md:text-2xl tabular-nums text-slate-900">{kg(d?.total_produksi_kg)}</div>
          <div className="text-[11px] text-slate-500 mt-0.5" data-testid="a-produk-selected">{selNama}</div>
          <table className="w-full mt-3 text-[11px]" data-testid="a-produk-table">
            <tbody>
              {[{ produk_id: "", nama: "Semua Produk", produksi_kg: perProduk.reduce((a, p) => a + Number(p.produksi_kg || 0), 0) }, ...perProduk].map((p) => {
                const active = produkId === p.produk_id;
                return (
                  <tr key={p.produk_id || "all"} onClick={() => setProdukId(p.produk_id)}
                    data-testid={`a-produk-row-${p.produk_id || "all"}`}
                    className={`cursor-pointer border-t border-slate-100 ${active ? "bg-blue-50 text-blue-800 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>
                    <td className="py-1 pr-2 truncate max-w-[140px]" title={p.nama}>{active ? "● " : ""}{p.nama}</td>
                    <td className="py-1 text-right font-mono whitespace-nowrap">{Number(p.produksi_kg || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })} Kg</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Stat testid="a-stat-sales" label={`Total Terjual · ${selNama}`} value={kg(d?.total_sales_kg)} icon={Scale} tone="bg-emerald-600"/>
        <Stat testid="a-stat-stok" label={`Sisa Stok · ${selNama}`} value={kg(d?.sisa_stok_kg)} icon={Warehouse} tone="bg-indigo-600"/>
        <Stat testid="a-stat-hpp" label={`HPP Rata-rata · ${selNama}`} value={rupiah(d?.hpp_rata)} unit="/Kg" icon={Package} tone="bg-slate-700"/>
      </div>

      <div className="card-flat p-4 md:p-6" data-testid="arang-trend-chart">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-base md:text-lg font-semibold text-slate-900" data-testid="arang-chart-title">Tren Produksi vs Penjualan — {selNama} · {rangeLabel(range, "12 Bulan Terakhir")}</h3>
            <p className="text-xs md:text-sm text-slate-500">Bar = Kg produksi & penjualan {trend[0]?.granularity === "day" ? "harian" : "bulanan"}. Line = Sisa stok kumulatif.</p>
          </div>
          <ChartRangeFilter value={range} onChange={setRange} defaultLabel="12 Bulan Terakhir" testid="arang-chart-filter"/>
        </div>
        <div style={{ width: "100%", minHeight: 320 }}>
          <ResponsiveContainer width="100%" height={320} initialDimension={{ width: 800, height: 320 }}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
              <XAxis dataKey="name" stroke="#64748B" style={{ fontSize: 11 }} minTickGap={8}/>
              <YAxis stroke="#64748B" style={{ fontSize: 11 }} tickFormatter={(v)=>`${v}`}/>
              <Tooltip formatter={(v)=>`${Number(v).toLocaleString("id-ID")} Kg`} labelFormatter={(_l, p)=>p?.[0]?.payload?.full || _l} contentStyle={{ fontSize: 12, borderRadius: 4, borderColor: "#166db4" }}/>
              <Legend wrapperStyle={{ fontSize: 12 }}/>
              <Bar dataKey="produksi" name="Produksi (Kg)" fill="#166db4" radius={[3,3,0,0]}/>
              <Bar dataKey="penjualan" name="Penjualan (Kg)" fill="#10B981" radius={[3,3,0,0]}/>
              <Line type="monotone" dataKey="stok" name="Stok Kumulatif" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }}/>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  </>);
}
